/**
 * Platform Governance Center — Phase 1.4 + 7 v7.3
 *
 * Refonte : 7 sections administratives (IAM · Compliance · Audit · Vault · KMS ·
 * Infrastructure · Monitoring) sans charts, focus tables/forms/drawers.
 *
 * Règle Phase 7 : SIEM = Administrer. Pas de temps réel. Pas de charts.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHeader, KpiRow } from "@/components/PageHeader";
import {
  Shield, User, Stethoscope, Cpu, History, Lock, KeyRound,
  Scale, FileCheck, Server, Database, Network, Activity, Settings,
  AlertTriangle, ListChecks, ArrowRight, GitBranch, Box, FileText
} from "lucide-react";
import { DOMAINS } from "@/lib/design-system";
import { api } from "@/lib/api";
import { safe } from "@/lib/safe-fetch";
import { trackCenter } from "@/lib/track-center";

export const Route = createFileRoute("/admin/siem/")({
  component: GovernanceCenter,
});

function GovernanceCenter() {
  const [data, setData] = useState<any>({});

  useEffect(() => {
    trackCenter("governance");
    Promise.all([
      safe(() => api.auditStats(), "governance.auditStats"),
      safe(() => api.observabilityStatus(), "governance.observabilityStatus"),
      safe(() => api.health(), "governance.health"),
    ]).then(([a, o, h]) => setData({ a, o, h }));
  }, []);

  // 4 KPIs administratifs
  const topKpis = [
    { label: "Utilisateurs actifs", value: 12, delta: 0, intent: "neutral" as const, icon: User },
    { label: "Politiques actives", value: 18, delta: +2, intent: "good" as const, icon: Scale },
    { label: "Compliance score", value: "94%", delta: +1, intent: "good" as const, icon: FileCheck },
    { label: "Secrets Vault", value: 47, delta: +3, intent: "good" as const, icon: Lock },
  ];

  const sections = [
    {
      id: "iam",
      title: "Identity & Access",
      icon: User,
      description: "Utilisateurs · RBAC · MFA · Médecins · Cotations",
      to: "/admin/users",
      modules: [
        { to: "/admin/users", label: "Utilisateurs", sub: "Comptes + RBAC + MFA" },
        { to: "/admin/doctors", label: "Médecins", sub: "INPE + spécialité" },
        { to: "/admin/doctor-cotations", label: "Cotations", sub: "NGAP par praticien" },
      ],
    },
    {
      id: "compliance",
      title: "Compliance",
      icon: Scale,
      description: "HIPAA · GDPR · IEC 62304 · ISO 14971 · FDA",
      to: "/admin/siem/conformite/hipaa-gdpr",
      modules: [
        { to: "/admin/siem/conformite/hipaa-gdpr", label: "HIPAA · GDPR", sub: "Privacy + Sécurité" },
        { to: "/admin/siem/conformite/iec-62304", label: "IEC 62304", sub: "Logiciels DM" },
        { to: "/admin/siem/conformite/iso-14971", label: "ISO 14971", sub: "Risk management" },
        { to: "/admin/siem/conformite/fda", label: "FDA 21 CFR 11", sub: "Electronic records" },
      ],
    },
    {
      id: "audit",
      title: "Audit Archive",
      icon: History,
      description: "WORM · Hash chain · Immutable · Retention 7 ans",
      to: "/admin/audit",
      modules: [
        { to: "/admin/audit", label: "Audit WORM", sub: "Hash chain integrity" },
        { to: "/admin/siem/gouvernance/regulatory-ledger", label: "Regulatory Ledger", sub: "Journal réglementaire" },
        { to: "/admin/siem/gouvernance/etablissements", label: "Établissements", sub: "Multi-tenant config" },
      ],
    },
    {
      id: "vault",
      title: "Vault & KMS",
      icon: Lock,
      description: "Coffre-fort · Clés cryptographiques · Rotation",
      to: "/security/secrets",
      modules: [
        { to: "/security/secrets", label: "Vault", sub: "47 secrets stockés" },
        { to: "/security/keys", label: "KMS", sub: "Clés A256GCM + rotation" },
        { to: "/admin/siem/infrastructure/vault", label: "Vault infrastructure", sub: "Cluster HashiCorp" },
      ],
    },
    {
      id: "infra",
      title: "Infrastructure",
      icon: Server,
      description: "Kubernetes · PostgreSQL · Redis · Kafka · ArgoCD",
      to: "/admin/siem/infrastructure/kubernetes",
      modules: [
        { to: "/admin/siem/infrastructure/kubernetes", label: "Kubernetes", sub: "Cluster prod" },
        { to: "/admin/siem/infrastructure/postgresql", label: "PostgreSQL", sub: "Supabase primary" },
        { to: "/admin/siem/infrastructure/redis", label: "Redis", sub: "Upstash cache + pub/sub" },
        { to: "/admin/siem/infrastructure/kafka", label: "Kafka", sub: "Event streaming" },
        { to: "/admin/siem/infrastructure/argocd", label: "ArgoCD", sub: "GitOps deploy" },
      ],
    },
    {
      id: "monitoring",
      title: "Monitoring & Observability",
      icon: Activity,
      description: "Prometheus · Grafana · Loki · Alertmanager · SLO",
      to: "/admin/siem/observabilite/grafana",
      modules: [
        { to: "/admin/siem/observabilite/grafana", label: "Grafana", sub: "Dashboards ops" },
        { to: "/admin/siem/observabilite/prometheus", label: "Prometheus", sub: "Métriques + alertes" },
        { to: "/admin/siem/observabilite/loki", label: "Loki", sub: "Logs centralisés" },
        { to: "/admin/siem/observabilite/alertmanager", label: "Alertmanager", sub: "Routing + silences" },
        { to: "/admin/siem/observabilite/slo", label: "SLO", sub: "Objectifs niveau service" },
      ],
    },
    {
      id: "machines",
      title: "Machines & Templates",
      icon: Cpu,
      description: "Configuration · Calibration · Auto-test · Templates",
      to: "/admin/machines",
      modules: [
        { to: "/admin/machines", label: "Machines", sub: "Templates + calibration" },
        { to: "/admin/consultations", label: "Consultations atomiques", sub: "Optimistic locking" },
        { to: "/admin/settings", label: "Settings", sub: "Drawer configuration" },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4 md:p-6 space-y-4">
      <PageHeader
        title="Platform Governance Center"
        description="IAM · Compliance · Audit · Vault · Infrastructure · Monitoring"
        domain="governance"
        actions={
          <>
            <Badge variant="outline" className="border-amber-500/30 text-amber-400">
              <AlertTriangle className="h-3 w-3 mr-1" /> Audit chain intact
            </Badge>
            <Button variant="outline" size="sm" asChild>
              <Link to="/admin/siem/notifications">Notifications</Link>
            </Button>
            <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-slate-900">
              + Nouvelle politique
            </Button>
          </>
        }
      >
        <KpiRow items={topKpis} domain="governance" />
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {sections.map((s) => {
          const Icon = s.icon;
          const d = DOMAINS.governance;
          return (
            <Card key={s.id} className={`bg-slate-900/60 border-slate-800 hover:${d.borderClass} transition`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`h-9 w-9 rounded-lg ${d.bgClass} ${d.textClass} flex items-center justify-center`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{s.title}</CardTitle>
                      <p className="text-xs text-slate-500">{s.description}</p>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {s.modules.map((m, j) => (
                  <Link key={j} to={m.to}>
                    <div className="flex items-center justify-between bg-slate-950/40 hover:bg-slate-800/60 transition rounded-lg p-2.5 border border-slate-800">
                      <div>
                        <div className="text-xs font-semibold text-slate-200">{m.label}</div>
                        <div className="text-[10px] text-slate-500">{m.sub}</div>
                      </div>
                      <ArrowRight className="h-3.5 w-3.5 text-slate-500" />
                    </div>
                  </Link>
                ))}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}